// ============================================================================
//  Tipos de gases — compartidos entre servidor y cliente (navegador).
// ============================================================================
export type GasKey = 'co' | 'co2' | 'o2' | 'no2';

export type GasClass = 'normal' | 'atencion' | 'peligro' | 'sin_dato';

export const GAS_KEYS: readonly GasKey[] = ['co', 'co2', 'o2', 'no2'];

/** Parámetros AMBIENTALES de un sensor de gas (2026-10-16): se publican en el
 *  mismo grupo de tópicos (`…/sensor-gas/temperatura`, `…/humedad`), pero no
 *  son gases — no tienen umbrales ni entran en ninguna decisión (nivel, modo
 *  seguro, LED, alertas). Solo se configuran, se descubren y se muestran. */
export type AmbientKey = 'temperature' | 'humidity';

export const AMBIENT_KEYS: readonly AmbientKey[] = ['temperature', 'humidity'];

/** Lo que puede medir un tópico de un sensor de gas. */
export type SensorVariable = GasKey | AmbientKey;

/** Todo lo que se guarda en el histórico de un sensor (gas_samples /
 *  gas_hourly): los gases y su temperatura/humedad. */
export const SENSOR_KEYS: readonly SensorVariable[] = [...GAS_KEYS, ...AMBIENT_KEYS];

export interface GasThreshold {
  atencion: number;
  peligro: number;
  direction: 'high' | 'low'; // high = peligro por exceso; low = peligro por defecto (O2)
  unit: string;
}

/** Estado de gases de UNA bocamina — el gas ya no se agrupa por "detector",
 *  se rige directo por bocamina (ver control.service.ts::gasStates). `portal`
 *  puede ser '' para el estado agregado de lo que no tiene bocamina asignada. */
export interface PortalGasSnapshot {
  portal: string;
  values: Partial<Record<GasKey, number>>;
  classes: Record<GasKey, GasClass>;
  configured: GasKey[];
  overall: GasClass;
  statusLight: 'verde' | 'amarillo' | 'rojo' | 'gris';
  stale: boolean;
  lastUpdate: number; // epoch seconds
}

export interface GasesSnapshot {
  overall: GasClass;
  stale: boolean;
  statusLight: 'verde' | 'amarillo' | 'rojo' | 'gris';
  values: Partial<Record<GasKey, number>>;
  classes: Record<GasKey, GasClass>;
  configured: GasKey[];
  lastUpdate: number;
  /** Por bocamina: el agregado (peor caso) de sus sensores que CONTROLAN —
   *  lo que decide su ventilación. */
  portals: Record<string, PortalGasSnapshot>;
  /** Cada sensor por separado, por id (como string), controle o no. */
  sensors: Record<string, GasSensorSnapshot>;
  /** Voladuras EN CURSO por bocamina (ver modules/gas/blast-tracker.ts); la
   *  bocamina sale de acá cuando se despeja. */
  blasts: Record<string, GasBlast>;
}

/** Una voladura detectada por el CO: el estallido en un sensor (origen) y el
 *  paso de esa nube por otro sensor de la misma bocamina (llegada). Solo
 *  hechos medidos; todos los instantes en epoch s del reloj del servidor (el
 *  mismo de gas_samples, así el crudo de /gas-history se alinea con ellos). */
export interface GasBlast {
  portal: string;
  originSensorId: number;
  originName: string;
  /** Cuándo el CO del origen cruzó BLAST_CLEAR_PPM en la subida que lo llevó
   *  a BLAST_ONSET_PPM. */
  startedAt: number;
  originPeakPpm: number;
  /** Primer otro sensor de la bocamina que cruzó BLAST_CLEAR_PPM; null = la
   *  nube todavía no llegó a ninguno. */
  arrivalSensorId: number | null;
  arrivalName: string | null;
  arrivedAt: number | null;
  arrivalPeakPpm: number | null;
  /** Todos los sensores de la bocamina bajo BLAST_CLEAR_PPM; null = en curso. */
  clearedAt: number | null;
}

/** Una fila de `blasts` (GET /api/blasts). */
export interface GasBlastRow extends GasBlast {
  id: number;
  portalId: number | null;
  /** Hay grabación de la escena desde el inicio (blast_frames, GET
   *  /api/blasts/:id/frames). false en las anteriores a la grabación, las del
   *  relleno y las que se retomaron tras un reinicio a mitad. */
  recorded: boolean;
}

/** Un sensor de gas (gas_sensors): su estado con su identidad. `portal` es la
 *  bocamina del sensor. */
export interface GasSensorSnapshot extends PortalGasSnapshot {
  sensorId: number;
  name: string;
  /** false = no controla: se ve y alerta, nunca mueve el ventilador. */
  controls: boolean;
  /** Temperatura/humedad del sensor: una clave por parámetro CONFIGURADO
   *  (ausente = el sensor no lo tiene); `null` = sin dato o vencido
   *  (gasStaleSeconds), nunca un 0 inventado. */
  ambient: Partial<Record<AmbientKey, number | null>>;
  /** Epoch s desde que el sensor está en PELIGRO de corrido (un instante fuera
   *  de peligro reinicia el episodio); `null` si no está en peligro. */
  dangerSince: number | null;
}
